import { ref } from "vue";
import type { Ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

// ─── Constantes de decodificação EAN-13 de balança (espelha o backend) ───────
const COMPRIMENTO_EAN_BALANCA = 13;
const PREFIXO_EAN_BALANCA = "2";
const PESO_BALANCA_INICIO = 7;  // 0-based
const PESO_BALANCA_FIM = 12;    // 0-based, exclusive → posições 8-12 em 1-based
const PESO_BALANCA_DIVISOR = 1000;

/** Dados retornados ao chamador após validação bem-sucedida. */
export interface DadosValidacaoDun14 {
	sucesso: true;
	codigoBarrasValidado: string;
	pesoRetornado: number | null;
	tipoAutomacao: "DUN14" | "EAN13" | "PESO_VARIAVEL";
}

/**
 * Classe de estado do modal `ModalAuditoriaVolumeDun14`.
 *
 * Encapsula apenas **reatividade e estado puro** — sem acesso a APIs Nuxt,
 * sem chamadas HTTP e sem áudio. Toda a lógica de efeitos colaterais
 * fica no composable `useAuditoriaVolumeDun14`.
 *
 * Seguindo o padrão FPROC_* do HarpiaWMS:
 * - A instância é criada no `<script setup>` do componente/página que a usa
 * - Se um filho precisar da instância, o pai faz `provide` e o filho faz `inject`
 */
export class FModalAuditoriaVolumeDun14 {
	// ── Ref de template ─────────────────────────────────────────────────────
	inputDun14Ref: Ref<ComponentExposed<unknown> | null> = ref(null);
	inputPesoRef: Ref<ComponentExposed<unknown> | null> = ref(null);

	// ── Visibilidade ─────────────────────────────────────────────────────────
	/** Visibilidade do modal principal. */
	visible = ref(false);
	/** Visibilidade do sub-diálogo de erro (msgDlg interno). */
	erroDialogVisible = ref(false);
	/** Visibilidade do sub-diálogo de dupla confirmação de peso (Regra 7). */
	confirmacaoDialogVisible = ref(false);

	// ── Estado dos inputs ────────────────────────────────────────────────────
	/** Código de barras digitado/bipado pelo operador (DUN14 ou EAN13). */
	codigoBipado = ref("");
	/**
	 * Peso numérico para o modo Peso Variável.
	 * É preenchido automaticamente quando o código bipado é um EAN-13 de balança.
	 */
	pesoInformado = ref<number>(0);

	// ── Estado de carregamento ───────────────────────────────────────────────
	isLoading = ref(false);

	// ── Mensagem de erro ─────────────────────────────────────────────────────
	erroMensagem = ref("");
	/**
	 * Sinal reativo para reprodução de áudio de erro.
	 * O `.vue` observa este valor e toca o áudio quando `true`,
	 * depois reseta para `false` — evitando instâncias em composables.
	 */
	deveTocarAudio = ref(false);

	// ── Resultado intermediário ──────────────────────────────────────────────
	/** Faixa de tolerância retornada pelo backend (Regra 7). */
	faixaTolerancia = ref<{ minima: number; maxima: number } | null>(null);
	/** Peso decodificado localmente a partir do EAN-13 de balança (Regra 5). */
	pesoDecodificadoLocal = ref<number | null>(null);

	// ── Callbacks ────────────────────────────────────────────────────────────
	/** Chamado após validação bem-sucedida — notifica o chamador. */
	onValidado: ((dados: DadosValidacaoDun14) => void) | null = null;
	/** Chamado quando o operador cancela sem concluir. */
	onCancelado: (() => void) | null = null;

	// ── Contexto do volume em conferência ─────────────────────────────────────
	/**
	 * Dados do volume a ser validado.
	 * Definidos no momento em que o chamador invoca `abrir()`.
	 * O modal filho lê esses valores diretamente da instância injetada.
	 */
	empresaId = ref<number>(0);
	mercadoriaId = ref<number>(0);
	isPesoVariavel = ref<boolean>(false);

	// ────────────────────────────────────────────────────────────────────────
	// Métodos públicos
	// ────────────────────────────────────────────────────────────────────────

	/**
	 * Abre o modal para validação de DUN14/EAN13 ou Peso Variável.
	 *
	 * @param contexto - Dados do volume a validar (empresa, mercadoria, tipo)
	 * @param callbacks - Funções de retorno ao concluir ou cancelar
	 */
	abrir(
		contexto: { empresaId: number; mercadoriaId: number; isPesoVariavel: boolean },
		callbacks: {
			onValidado: (dados: DadosValidacaoDun14) => void;
			onCancelado?: () => void;
		},
	): void {
		this.empresaId.value = contexto.empresaId;
		this.mercadoriaId.value = contexto.mercadoriaId;
		this.isPesoVariavel.value = contexto.isPesoVariavel;
		this.onValidado = callbacks.onValidado;
		this.onCancelado = callbacks.onCancelado ?? null;
		this.resetar();
		this.visible.value = true;
	}

	/** Fecha o modal sem notificar sucesso. */
	cancelar(): void {
		this.visible.value = false;
		this.onCancelado?.();
	}

	/**
	 * Tenta decodificar o código bipado como EAN-13 de balança comercial.
	 *
	 * Posições 8-12 em 1-based → slice(7, 12) em JS (0-based, exclusive).
	 * Os 5 chars representam o peso em gramas → divide por 1000 para Kg.
	 *
	 * Espelha a Regra 5 do backend para feedback visual imediato
	 * antes do round-trip HTTP.
	 *
	 * @returns `true` se o código foi reconhecido como EAN de balança
	 */
	tentarDecodificarEanBalanca(codigo: string): boolean {
		if (
			codigo.length === COMPRIMENTO_EAN_BALANCA &&
			codigo.startsWith(PREFIXO_EAN_BALANCA)
		) {
			const gramas = parseInt(codigo.slice(PESO_BALANCA_INICIO, PESO_BALANCA_FIM), 10);
			const peso = gramas / PESO_BALANCA_DIVISOR;
			this.pesoDecodificadoLocal.value = peso;
			this.pesoInformado.value = peso;
			return true;
		}
		return false;
	}

	/** Ativa o sub-diálogo de dupla confirmação de peso (chamado pelo composable). */
	ativarConfirmacaoPeso(faixa: { minima: number; maxima: number }): void {
		this.faixaTolerancia.value = faixa;
		this.confirmacaoDialogVisible.value = true;
	}

	/** Confirma o peso após o diálogo de dupla confirmação (Regra 7). */
	confirmarPeso(): void {
		this.confirmacaoDialogVisible.value = false;
		this.onValidado?.({
			sucesso: true,
			codigoBarrasValidado: this.codigoBipado.value,
			pesoRetornado: this.pesoInformado.value,
			tipoAutomacao: "PESO_VARIAVEL",
		});
		this.visible.value = false;
	}

	/** Recusa a confirmação de peso — volta para o input. */
	recusarConfirmacaoPeso(): void {
		this.confirmacaoDialogVisible.value = false;
		this.pesoInformado.value = 0;
		this.pesoDecodificadoLocal.value = null;
	}

	/** Exibe o sub-diálogo de erro com a mensagem informada. */
	mostrarErro(mensagem: string, tocarAudio = false): void {
		this.erroMensagem.value = mensagem;
		this.erroDialogVisible.value = true;
		if (tocarAudio) this.deveTocarAudio.value = true;
	}

	/** Fecha o sub-diálogo de erro. */
	fecharErroDialog(): void {
		this.erroDialogVisible.value = false;
	}

	/** Notifica o chamador com sucesso e fecha o modal. */
	concluirComSucesso(dados: DadosValidacaoDun14): void {
		this.visible.value = false;
		this.onValidado?.(dados);
	}

	/** Reseta todos os campos de input para o estado inicial. */
	resetar(): void {
		this.codigoBipado.value = "";
		this.pesoInformado.value = 0;
		this.pesoDecodificadoLocal.value = null;
		this.faixaTolerancia.value = null;
		this.erroMensagem.value = "";
		this.erroDialogVisible.value = false;
		this.confirmacaoDialogVisible.value = false;
		this.isLoading.value = false;
		this.deveTocarAudio.value = false;
	}
}
